/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  WebsocketListingProperties,
  WebsocketListingRequest,
  WebsocketListingResponse,
  WebsocketInitialProperties,
} from '../../interface/blocked-neverblocked.interface';
import { CommonFilterProperties } from '../../interface/filter.interface';
import { ConfigureListFilterResponse } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import device from '../../utils/api/device';

// a thunk to fetch users
export const fetchBlockedWebsocket = createAsyncThunk<
  WebsocketListingResponse,
  WebsocketListingRequest
>(
  'device-management-BlockedWebsocket/fetchDevicesBlockedWebsocket',
  async (data: WebsocketListingRequest, { dispatch }) => {
    try {
      const response = await device.fetchBlockedWebsocket(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

export const fetchBlockedWebsocketFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'BlockedWebsocket/fetchListPerformanceStationFilters',
  async (_, { dispatch }) => {
    try {
      const response = await device.fetchBlockedWebsocket();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// Initial state
const initialState: WebsocketInitialProperties = {
  devices: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  selectedChargingPoint: null,
  loading: false,
  deviceListLoading: false,
  filters: [],
  searchBy: '',
};

// Create the slice
export const BlockedWebsocketSlice = createSlice({
  name: 'device-management-BlockedWebsocket',
  initialState,
  reducers: {
    /**
     * Function to update selectedChargingPoint
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing the selected device.
     */
    setSelectedDevice: (state, action) => {
      state.selectedChargingPoint = action.payload;
    },
    /**
     * setVlbcSearchBy function to update the searchBy property in vlbcs state
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing the search query.
     */
    setVlbcSearchBy: (state, action) => {
      state.searchBy = action.payload;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The action reducer map builder.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlockedWebsocket.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBlockedWebsocket.fulfilled, (state, action) => {
        state.loading = false;
        state.devices = action.payload?.results?.length
          ? action.payload.results?.map((eachDevice: WebsocketListingProperties) => ({
              ...eachDevice,
              id: eachDevice?._id,
              connector1: eachDevice?.connectorsStatus?.connector1 || '',
              connector2: eachDevice?.connectorsStatus?.connector2 || '',
              blockUntil: ConvertUTCtoDate(eachDevice.blockUntil || ''),
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchBlockedWebsocket.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
  },
});

// Export the actions
export const { setVlbcSearchBy, setSelectedDevice } = BlockedWebsocketSlice.actions;
// Export the reducer
export default BlockedWebsocketSlice.reducer;
